/**
 * @file        src/pages/package/AddPackage.jsx
 * @description Create / edit a holiday package. Images are stored as URLs:
 *              one hero image + an optional gallery (one URL per line).
 *              Reused for /package/new and /package/:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-09-15 Claude — Duration is validated as a real duration, and Language
 *                          is a locked English-only dropdown.
 *                          Days/Nights were each independently 0–365 and both
 *                          OPTIONAL, so "0 days / 9 nights" saved happily and a
 *                          package could be published with no duration at all.
 *                          Days is now required and >= 1, and Nights must be
 *                          days-1 or days — the only two shapes a real itinerary
 *                          takes. Mirrored server-side in packages.routes.js;
 *                          this schema is a convenience, not the boundary.
 *                          Language was a free-text <input maxLength={8}> that
 *                          accepted any string for an English-only storefront.
 *   - 2026-07-01 Claude — Initial (Package Management: Package List slice).
 *   - 2026-10-09 Claude — Search & social block (components/SeoFields.jsx,
 *                          migration 077): meta title/description/keywords,
 *                          share image, canonical override, index toggle and a
 *                          server-resolved search preview. Every SEO key is sent
 *                          on save so clearing one actually clears it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import ImageUpload from '../../components/ImageUpload';
import SeoFields, { SEO_EMPTY, seoFromRow, seoBody, seoErrors, previewSlug } from '../../components/SeoFields';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from '../../lib/languages';

const urlish = z.string().trim().url('Must be a valid URL');

const schema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  category_id: z.union([z.coerce.number().int().positive(), z.literal('')]).optional(),
  destination: z.string().trim().max(160).optional(),
  hero_image: z.union([urlish, z.literal('')]).optional(),
  gallery: z.string().optional(),
  duration_days: z.coerce.number({ invalid_type_error: 'Required' })
    .int('Whole days only').min(1, 'Must be at least 1 day').max(365, 'Cannot exceed 365 days'),
  duration_nights: z.coerce.number({ invalid_type_error: 'Required' })
    .int('Whole nights only').min(0, 'Cannot be negative').max(365, 'Cannot exceed 365 nights'),
  price: z.union([z.coerce.number().min(0), z.literal('')]).optional(),
  description: z.string().trim().max(5000).optional(),
  language: z.enum(['en'], { errorMap: () => ({ message: 'English only' }) }),
  is_active: z.boolean(),
})
  // A trip of N days is N-1 or N nights — anything else is a typo, and the
  // website prints the pair verbatim ("6 Days / 9 Nights" is not a package).
  .refine((d) => d.duration_nights === d.duration_days || d.duration_nights === d.duration_days - 1, {
    message: 'Nights must be the same as days, or one less (e.g. 6 days / 5 nights).',
    path: ['duration_nights'],
  });

const empty = {
  name: '', category_id: '', destination: '', hero_image: '', gallery: '',
  duration_days: '', duration_nights: '', price: '', description: '',
  language: DEFAULT_LANGUAGE, is_active: true,
};

export default function AddPackage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [seo, setSeo] = useState(SEO_EMPTY);
  const [errors, setErrors] = useState({});

  const { data: cats } = useQuery({
    queryKey: qk.packageCategories.list({ pageSize: 200 }),
    queryFn: () => get('/package-categories', { params: { pageSize: 200 } }),
  });
  const categories = cats?.data || [];

  const { data: existing } = useQuery({
    queryKey: qk.packages.one(id),
    queryFn: () => get(`/packages/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const p = existing.data;
      setForm({
        name: p.name || '',
        category_id: p.category_id || '',
        destination: p.destination || '',
        hero_image: p.hero_image || '',
        gallery: Array.isArray(p.images) ? p.images.join('\n') : '',
        duration_days: p.duration_days ?? '',
        duration_nights: p.duration_nights ?? '',
        price: p.price ?? '',
        description: p.description || '',
        language: p.language || DEFAULT_LANGUAGE,
        is_active: !!p.is_active,
      });
      setSeo(seoFromRow(p));
    }
  }, [existing]);

  // The draft the server resolves the search preview from. Every save sends
  // `name`, and packages.service re-derives the slug from it, so the slug the
  // page will have after saving is the name's slug, on create and edit alike.
  const previewItem = useMemo(() => ({
    id: existing?.data?.id,
    name: form.name,
    slug: previewSlug(form.name) || existing?.data?.slug,
    description: form.description,
    hero_image: form.hero_image,
    images: (form.gallery || '').split('\n').map((s) => s.trim()).filter(Boolean),
    price: form.price === '' ? undefined : Number(form.price),
    created_at: existing?.data?.created_at,
    updated_at: existing?.data?.updated_at,
  }), [form, existing]);

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? patch(`/packages/${id}`, body) : post('/packages', body)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Package updated' : 'Package added', `${form.name} saved.`);
      qc.invalidateQueries({ queryKey: ['packages'] });
      navigate('/package/list');
    },
    onError: (err) => {
      const fields = err.response?.data?.error?.fields;
      if (fields) setErrors(fields);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function buildBody(data) {
    const gallery = (data.gallery || '')
      .split('\n').map((s) => s.trim()).filter(Boolean);
    const body = {
      name: data.name,
      language: data.language,
      is_active: data.is_active,
      // Always send the image fields, even when empty. A PATCH that OMITS a field leaves
      // the stored value untouched, so "Remove" (hero_image → '') or clearing the gallery
      // (images → []) has to send the empty value explicitly, or the change never persists.
      hero_image: data.hero_image || '',
      images: gallery,
      ...seoBody(seo),
    };
    if (data.category_id !== '' && data.category_id != null) body.category_id = Number(data.category_id);
    if (data.destination) body.destination = data.destination;
    body.duration_days = data.duration_days;
    body.duration_nights = data.duration_nights;
    if (data.price !== '') body.price = Number(data.price);
    if (data.description) body.description = data.description;
    return body;
  }

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    const fieldErrs = seoErrors(seo);
    if (!parsed.success) {
      for (const i of parsed.error.issues) fieldErrs[i.path.join('.')] = i.message;
    }
    if (!parsed.success || Object.keys(fieldErrs).length) return setErrors(fieldErrs);
    setErrors({});
    mut.mutate(buildBody(parsed.data));
  }

  function onChange(key) {
    return (e) => setForm({ ...form, [key]: e.target.value });
  }

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Package' : 'Add Package'}
        subtitle="Package Management"
        actions={<button className="btn ghost" onClick={() => navigate('/package/list')}><Icon name="x" /> Cancel</button>}
      />

      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Package Name" required err={errors.name}>
            <input value={form.name} onChange={onChange('name')} placeholder="e.g. Bali Bliss 5N/6D" />
          </Field>
          <Field label="Category" err={errors.category_id}>
            <Select
              value={form.category_id}
              onChange={(v) => setForm({ ...form, category_id: v })}
              placeholder="— None —"
              options={[{ value: '', label: '— None —' }, ...categories.map((c) => ({ value: c.id, label: c.name }))]}
            />
          </Field>
          <Field label="Destination" err={errors.destination}>
            <input value={form.destination} onChange={onChange('destination')} placeholder="e.g. Bali, Indonesia" />
          </Field>
          <Field label="Language" required err={errors.language}
                 hint="English is the only language the website serves.">
            <Select
              value={form.language}
              onChange={(v) => setForm({ ...form, language: v })}
              options={LANGUAGE_OPTIONS}
            />
          </Field>

          <Field label="Hero Image" err={errors.hero_image} full>
            <ImageUpload value={form.hero_image} onChange={(url) => setForm({ ...form, hero_image: url })} />
          </Field>

          <Field label="Gallery Image URLs" err={errors.gallery} full
                 hint="Optional. One image URL per line.">
            <textarea rows={3} value={form.gallery} onChange={onChange('gallery')}
                      placeholder={'https://…/1.jpg\nhttps://…/2.jpg'} />
          </Field>

          <Field label="Duration (Days)" required err={errors.duration_days}>
            <input type="number" min={1} max={365} value={form.duration_days}
                   onChange={onChange('duration_days')} placeholder="6" />
          </Field>
          <Field label="Duration (Nights)" required err={errors.duration_nights}
                 hint="Same as days, or one less.">
            <input type="number" min={0} max={365} value={form.duration_nights}
                   onChange={onChange('duration_nights')} placeholder="5" />
          </Field>
          <Field label="Price (₹)" err={errors.price}>
            <input type="number" min={0} step="0.01" value={form.price} onChange={onChange('price')} placeholder="45000" />
          </Field>

          <Field label="Description" err={errors.description} full>
            <textarea rows={4} value={form.description} onChange={onChange('description')}
                      placeholder="Short package description shown on the website." />
          </Field>

          <Field label="Status">
            <label className="check">
              <input type="checkbox" checked={form.is_active}
                     onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Active (visible to the website)</span>
            </label>
          </Field>

          <SeoFields value={seo} onChange={setSeo} errors={errors} itemKind="package" item={previewItem} />

          <div className="form-actions">
            {!isEdit && (
              <button type="button" className="btn ghost" onClick={() => { setForm(empty); setSeo(SEO_EMPTY); }}>Reset</button>
            )}
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Package'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
